import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Switch } from '@/components/ui/switch';
import { Sparkles, Smartphone, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Coach Continuity Toggle
 * ------------------------------------------------------------------
 * Optional, opt-in cross-device sync for the agenda micro-coach.
 * Default OFF. Opting in stores a small per-user preferences doc
 * server-side; opting out hard-deletes that doc.
 *
 * Framing: "support continuity", NOT "behaviour tracking". The copy
 * reflects HealthLoop's local-first / preventative-health posture.
 *
 * Props:
 *   - userId: required to scope localStorage
 *   - getLocalSignals: () => object — read current local signals
 *   - applyServerSignals: (signals) => void — merge server snapshot
 *     back into local state (caller decides merge strategy)
 */
const COACH_OPT_IN_KEY = (uid) => `hl_coach_opted_in_v1_${uid || 'anon'}`;

export const CoachContinuityToggle = ({ userId, getLocalSignals, applyServerSignals }) => {
  const [optedIn, setOptedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Initial load: read server state and reconcile with local cache so the
  // toggle reflects the user's last decision regardless of device.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/me/coach-preferences`, { withCredentials: true });
        if (cancelled) return;
        const isOptedIn = !!res?.data?.opted_in;
        setOptedIn(isOptedIn);
        try {
          window.localStorage?.setItem(COACH_OPT_IN_KEY(userId), isOptedIn ? '1' : '0');
        } catch { /* storage unavailable */ }

        // If opted in and server has signals, hand them up so the parent
        // can merge with local. We never overwrite engagement counts —
        // the merge strategy lives in the parent (Profile.jsx).
        if (isOptedIn && res?.data?.signals) {
          applyServerSignals?.(res.data.signals);
        }
      } catch {
        // Any error → fall back to local cache so the toggle is still
        // accurate offline and the page never hangs on this control.
        try {
          const cached = window.localStorage?.getItem(COACH_OPT_IN_KEY(userId));
          if (!cancelled) setOptedIn(cached === '1');
        } catch { /* empty */ }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // applyServerSignals is intentionally omitted — we want this to run
    // exactly once per user-id, not whenever the parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleToggle = async (next) => {
    setSaving(true);
    setOptedIn(next);
    try {
      window.localStorage?.setItem(COACH_OPT_IN_KEY(userId), next ? '1' : '0');
    } catch { /* empty */ }

    try {
      if (next) {
        // Push current local signals up so the user's existing nuance
        // travels with them rather than starting from scratch.
        const signals = getLocalSignals?.() || {};
        await axios.put(
          `${API}/api/me/coach-preferences`,
          { opted_in: true, signals },
          { withCredentials: true },
        );
        toast.success('Continuity on. Your check-in cues will travel with you.', {
          description: 'You can turn this off any time.',
        });
      } else {
        // Hard-delete — the privacy promise is no server-side trace.
        await axios.delete(`${API}/api/me/coach-preferences`, { withCredentials: true });
        toast('Continuity off. Synced cues have been removed from our servers.');
      }
    } catch (e) {
      // Roll back the visual toggle if the network call failed.
      setOptedIn(!next);
      try {
        window.localStorage?.setItem(COACH_OPT_IN_KEY(userId), !next ? '1' : '0');
      } catch { /* empty */ }
      toast.error("Couldn't update continuity right now. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
      data-testid="profile-coach-continuity-card"
    >
      <div className="flex items-start gap-3">
        <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-700 shadow-sm">
          <Sparkles className="h-4 w-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-gray-900">Continuity across devices</p>
            {loading ? (
              <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
            ) : (
              <Switch
                checked={optedIn}
                onCheckedChange={handleToggle}
                disabled={saving}
                data-testid="profile-coach-continuity-toggle"
              />
            )}
          </div>
          <p className="mt-1 text-xs text-gray-500 leading-relaxed">
            Quietly remember which gentle check-in cues feel right for you, so the same
            small suggestions follow you between phone and laptop.
          </p>
          <ul className="mt-2.5 space-y-1.5 text-[11px] text-gray-500">
            <li className="flex items-start gap-1.5">
              <ShieldCheck className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />
              <span>Off by default. We never sync mood, journal, or reminder content.</span>
            </li>
            <li className="flex items-start gap-1.5">
              <Smartphone className="h-3 w-3 text-emerald-500 mt-0.5 shrink-0" />
              <span>Turning off removes synced cues from our servers immediately.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CoachContinuityToggle;
